/**
 * Daily Check-In Section
 *
 * Shared symptom tracker component used in both BeforeTreatment and InCare stages.
 * Provides consistent symptom logging with:
 * - Empty default state (no pre-selected symptoms)
 * - Green/selected styling with checkmark
 * - Grey/unselected styling
 * - Collapsed preview with gradient fade
 * - "View all/View fewer" centered button
 * - Immediate persistence to database with optimistic updates
 * - Timezone-safe date handling for "today"
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { View, Pressable, TextInput, ScrollView } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import {
  Activity,
  Utensils,
  HeartHandshake,
  Droplets,
  Droplet,
  Brain,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  useSymptoms,
  useCreateSymptoms,
  useDeleteSymptom,
} from "../useCalendarData";
import { toLocalDateString, getToday } from "@src/packages/utils/date";
import {
  BLEEDING_SYMPTOM_LABELS,
  canonicalBleedingLabel,
} from "../prediction/menstrualBleeding";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// ============================================================================
// Types
// ============================================================================

export interface DailyCheckInSectionProps {
  /** Whether to show the section header (default: true) */
  showHeader?: boolean;
  /** Custom header text (default: "Daily Check-in") */
  headerText?: string;
  /** Custom question text (default: "How are you feeling today?") */
  questionText?: string;
}

// ============================================================================
// Symptom Categories
// ============================================================================

interface SymptomCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  symptoms: string[];
}

const SYMPTOM_CATEGORIES: SymptomCategory[] = [
  {
    id: "digestion",
    label: "Digestion",
    icon: Utensils,
    symptoms: ["Bloating", "Nausea", "Cravings", "Indigestion"],
  },
  {
    id: "sexual",
    label: "Sexual & Reproductive",
    icon: HeartHandshake,
    symptoms: [
      "High Libido",
      "Low Libido",
      "Ovulation Pain",
      "Breast Tenderness",
    ],
  },
  {
    id: "urogenital",
    label: "Urogenital",
    icon: Droplets,
    symptoms: ["Frequent Urination", "Burning", "Incontinence"],
  },
  {
    id: "bleeding",
    label: "Bleeding & Clotting",
    icon: Droplet,
    // Canonical bleeding labels, shared with the Care check-in so one
    // vocabulary reaches the record. `menstrualBleeding` still reads the
    // bare "Light"/"Heavy" these once wrote, for rows already stored.
    symptoms: [
      BLEEDING_SYMPTOM_LABELS.spotting,
      BLEEDING_SYMPTOM_LABELS.light,
      BLEEDING_SYMPTOM_LABELS.heavy,
      "Clots",
    ],
  },
  {
    id: "psychological",
    label: "Psychological",
    icon: Brain,
    symptoms: ["Happy", "Anxious", "Irritable", "Fatigue", "Insomnia"],
  },
  {
    id: "heart",
    label: "Heart & Lungs",
    icon: Activity,
    symptoms: ["Palpitations", "Shortness of Breath"],
  },
];

// ============================================================================
// Component
// ============================================================================

export function DailyCheckInSection({
  showHeader = true,
  headerText = "Daily Check-in",
  questionText = "How are you feeling today?",
}: DailyCheckInSectionProps) {
  const capturePress = usePressCapture("DailyCheckInSection");
  // Get today's date for queries - timezone-safe
  const todayString = useMemo(() => toLocalDateString(getToday()), []);

  // Symptom state
  const [symptomNote, setSymptomNote] = useState("");
  const [isSymptomsExpanded, setIsSymptomsExpanded] = useState(false);
  // Optimistic state for immediate UI feedback
  const [optimisticSymptoms, setOptimisticSymptoms] = useState<Set<string>>(
    new Set(),
  );

  // Date navigation state (for future date switching)
  const [selectedDate, setSelectedDate] = useState(getToday());

  // Fetch today's symptoms
  const { data: todaySymptoms = [] } = useSymptoms({ date: todayString });

  // Build a map of symptom name -> symptom ID for deletion
  const symptomIdMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of todaySymptoms) {
      if (s.symptom && s.id) {
        // Keyed by the current label so a row stored under a retired one still
        // matches its chip — and can still be deleted by unticking it.
        map.set(canonicalBleedingLabel(s.symptom), s.id);
      }
    }
    return map;
  }, [todaySymptoms]);

  // Saved symptom names from DB
  const savedSymptomNames = useMemo(
    () => new Set(todaySymptoms.map((s) => canonicalBleedingLabel(s.symptom))),
    [todaySymptoms],
  );

  // Combined selection state: saved symptoms + optimistic additions - optimistic removals
  const selectedSymptoms = useMemo(() => {
    const combined = new Set(savedSymptomNames);
    // Apply optimistic updates
    for (const symptom of optimisticSymptoms) {
      if (savedSymptomNames.has(symptom)) {
        // Optimistic removal
        combined.delete(symptom);
      } else {
        // Optimistic addition
        combined.add(symptom);
      }
    }
    return combined;
  }, [savedSymptomNames, optimisticSymptoms]);

  // Clear optimistic state when saved data changes (query settled)
  useEffect(() => {
    setOptimisticSymptoms(new Set());
  }, [todaySymptoms]);

  // Symptom mutations
  const createSymptomsMutation = useCreateSymptoms();
  const deleteSymptomMutation = useDeleteSymptom();

  const isMutating =
    createSymptomsMutation.isPending || deleteSymptomMutation.isPending;

  // Toggle symptom with immediate persistence
  const toggleSymptom = useCallback(
    (symptom: string) => {
      const isCurrentlySaved = savedSymptomNames.has(symptom);
      const symptomId = symptomIdMap.get(symptom);

      // Optimistic UI update
      setOptimisticSymptoms((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(symptom)) {
          newSet.delete(symptom);
        } else {
          newSet.add(symptom);
        }
        return newSet;
      });

      if (isCurrentlySaved && symptomId) {
        // Delete the symptom
        deleteSymptomMutation.mutate(symptomId, {
          onError: () => {
            // Revert optimistic update on error
            setOptimisticSymptoms((prev) => {
              const newSet = new Set(prev);
              newSet.delete(symptom);
              return newSet;
            });
          },
        });
      } else if (!isCurrentlySaved) {
        // Create the symptom
        createSymptomsMutation.mutate(
          {
            date: todayString,
            symptoms: [symptom],
            notes: null,
          },
          {
            onError: () => {
              // Revert optimistic update on error
              setOptimisticSymptoms((prev) => {
                const newSet = new Set(prev);
                newSet.delete(symptom);
                return newSet;
              });
            },
          },
        );
      }
    },
    [
      savedSymptomNames,
      symptomIdMap,
      todayString,
      createSymptomsMutation,
      deleteSymptomMutation,
    ],
  );

  // Render a symptom chip with consistent styling
  const renderSymptomChip = (symptom: string) => {
    const isSelected = selectedSymptoms.has(symptom);
    return (
      <Pressable
        key={symptom}
        onPress={() => {
          capturePress(pressLabel(symptom), "symptom_chip");
          toggleSymptom(symptom);
        }}
        disabled={isMutating}
        style={{
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: isSelected
            ? "rgba(74, 222, 128, 0.6)"
            : "rgba(255, 255, 255, 0.1)",
          backgroundColor: isSelected
            ? "rgba(74, 222, 128, 0.25)"
            : "rgba(255, 255, 255, 0.05)",
          opacity: isMutating ? 0.7 : 1,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            color: isSelected ? "#4ADE80" : "#94A3B8",
          }}
        >
          {isSelected ? `✓ ${symptom}` : symptom}
        </Text>
      </Pressable>
    );
  };

  return (
    <View style={{ marginBottom: 24 }}>
      {/* Card */}
      <View
        style={{
          borderRadius: 24,
          position: "relative",
          overflow: "hidden",
          backgroundColor: "rgba(15, 23, 42, 0.8)",
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.1)",
        }}
      >
        {/* Header with title, subtitle, and date navigation */}
        <View
          style={{
            padding: 20,
            paddingBottom: 16,
            borderBottomWidth: 1,
            borderBottomColor: "rgba(255, 255, 255, 0.05)",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: "600",
                  color: "#E2E8F0",
                  marginBottom: 4,
                }}
              >
                {questionText}
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  color: "#94A3B8",
                  marginBottom: 2,
                }}
              >
                Daily Symptom Check-in
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: "#EAB308",
                }}
              >
                Today
              </Text>
            </View>
            {/* Date Navigation Arrows */}
            <View style={{ flexDirection: "row", gap: 8 }}>
              <Pressable
                onPress={() => {
                  capturePress("previous_day");
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 1);
                  setSelectedDate(newDate);
                }}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                }}
              >
                <ChevronLeft size={18} color="#94A3B8" />
              </Pressable>
              <Pressable
                onPress={() => {
                  capturePress("next_day");
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 1);
                  setSelectedDate(newDate);
                }}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                }}
              >
                <ChevronRight size={18} color="#94A3B8" />
              </Pressable>
            </View>
          </View>
        </View>

        {/* Content */}
        <View
          style={{ paddingHorizontal: 20, paddingTop: 20, paddingBottom: 16 }}
        >
          {/* Notes Input - always visible */}
          <TextInput
            value={symptomNote}
            onChangeText={setSymptomNote}
            placeholder="Add a note about today..."
            placeholderTextColor="#475569"
            style={{
              width: "100%",
              borderRadius: 12,
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              borderWidth: 1,
              borderColor: "rgba(255, 255, 255, 0.1)",
              paddingHorizontal: 14,
              paddingVertical: 10,
              fontSize: 14,
              fontWeight: "500",
              color: "#E2E8F0",
            }}
          />

          {/* Collapsible Content Container */}
          <View
            style={{
              position: "relative",
              maxHeight: isSymptomsExpanded ? undefined : 200,
              overflow: isSymptomsExpanded ? "visible" : "hidden",
            }}
          >
            {/* Symptom Categories */}
            <View style={{ paddingTop: 8 }}>
              {SYMPTOM_CATEGORIES.map((category) => {
                const CategoryIcon = category.icon;
                return (
                  <View key={category.id} style={{ marginTop: 20 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 12,
                      }}
                    >
                      <CategoryIcon size={12} color="#94A3B8" />
                      <Text
                        style={{
                          fontSize: 10,
                          fontWeight: "700",
                          color: "#64748B",
                          textTransform: "uppercase",
                          letterSpacing: 1.5,
                        }}
                      >
                        {category.label}
                      </Text>
                    </View>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={{ gap: 8 }}
                    >
                      {category.symptoms.map(renderSymptomChip)}
                    </ScrollView>
                  </View>
                );
              })}
            </View>

            {/* Gradient Fade Overlay - only when collapsed */}
            {!isSymptomsExpanded && (
              <LinearGradient
                colors={[
                  "transparent",
                  "rgba(15, 23, 42, 0.95)",
                  "rgba(15, 23, 42, 1)",
                ]}
                locations={[0, 0.6, 1]}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 80,
                }}
                pointerEvents="none"
              />
            )}
          </View>

          {/* Expand/Collapse Button - centered at bottom */}
          <View
            style={{
              alignItems: "center",
              paddingTop: 16,
            }}
          >
            <Pressable
              onPress={() => {
                capturePress(
                  isSymptomsExpanded ? "View fewer" : "View all",
                  isSymptomsExpanded ? "collapse_symptoms" : "expand_symptoms",
                );
                setIsSymptomsExpanded(!isSymptomsExpanded);
              }}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                paddingHorizontal: 20,
                paddingVertical: 8,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: "rgba(255, 255, 255, 0.1)",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              }}
            >
              <Text
                style={{ fontSize: 12, color: "#E2E8F0", fontWeight: "600" }}
              >
                {isSymptomsExpanded ? "View fewer" : "View all"}
              </Text>
              {isSymptomsExpanded ? (
                <ChevronUp size={14} color="#94A3B8" />
              ) : (
                <ChevronDown size={14} color="#94A3B8" />
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}
